<template>
  <div class="wrap">
    <!-- <header class="head">
      <h2 class="title">XP Arcade</h2>
      <button class="close" @click="$emit('close')" title="Close">✕</button>
    </header> -->

    <section class="grid">
      <!-- Progress Ring + Stats -->
      <div class="card ring-card">
        <div class="ring" :style="ringStyle">
          <div class="ring-inner">
            <div class="lvl">LV {{ level }}</div>
            <div class="xp">{{ displayedXp }} XP</div>
            <small>{{ Math.round(progress*100) }}% to next</small>
          </div>
        </div>
        <div class="stats">
          <div class="stat">
            <b>{{ totals.conversations }}</b>
            <small>Conversations</small>
          </div>
          <div class="stat">
            <b>{{ totals.messages }}</b>
            <small>Messages</small>
          </div>
          <div class="stat">
            <b>{{ totals.topics }}</b>
            <small>Topics</small>
          </div>
          <div class="stat">
            <b>{{ streak }}</b>
            <small>Day Streak</small>
          </div>
        </div>
      </div>

      <!-- Slot Machine -->
      <div class="card slot-card">
        <h3 class="slot-title">Spin for Bonus</h3>
        <div class="slots">
          <div class="reel" :class="{spinning: spinning}" :style="reelStyle(0)">
            <div class="strip" :style="stripStyle(0)">
              <div v-for="(s,i) in reelStrips[0]" :key="'r0'+i" class="face">{{ s }}</div>
            </div>
          </div>
          <div class="reel" :class="{spinning: spinning}" :style="reelStyle(1)">
            <div class="strip" :style="stripStyle(1)">
              <div v-for="(s,i) in reelStrips[1]" :key="'r1'+i" class="face">{{ s }}</div>
            </div>
          </div>
          <div class="reel" :class="{spinning: spinning}" :style="reelStyle(2)">
            <div class="strip" :style="stripStyle(2)">
              <div v-for="(s,i) in reelStrips[2]" :key="'r2'+i" class="face">{{ s }}</div>
            </div>
          </div>
        </div>
        <div class="slot-actions">
          <button class="spin" :disabled="spinning" @click="spin">SPIN</button>
          <div class="win" v-if="winText">{{ winText }}</div>
        </div>
        <canvas ref="confetti" class="confetti"></canvas>
      </div>

      <!-- Recent Activity -->
      <div class="card feed-card">
        <h3>Recent Activity</h3>
        <ul class="feed">
          <li v-for="(row,i) in recent" :key="i">
            <span class="when">{{ formatDate(row.updatedAt || row.createdAt) }}</span>
            <span class="title" :title="row.title">{{ row.title }}</span>
            <span class="chips">
              <em v-for="(k,ki) in (row.keywords || []).slice(0,3)" :key="ki">#{{ k }}</em>
            </span>
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script>
import { ArchiveAPI } from "@/utils/storage";

export default {
  name: "GamifyOverlay",
  data() {
    return {
      displayedXp: 0,
      animRAF: 0,
      spinning: false,
      winText: "",
      // slot symbols
      faces: ["💎","⭐","⚡","🎯","🚀","🧠","🎉","💡"],
      reelStrips: [[],[],[]],
      reelOffsets: [0,0,0],
      reelSpeeds: [0,0,0],
      stopTargets: [0,0,0],
      // stats
      recent: [],
      totals: { conversations: 0, messages: 0, topics: 0 },
      streak: 0,
    };
  },
  computed: {
    xp(){ return this.$store.state.xp; },
    level(){ return this.$store.state.level; },
    progress(){ return this.$store.getters.levelProgress; },
    ringStyle(){
      const pct = Math.min(99.999, Math.max(0.001, this.progress*100));
      return {
        background: `conic-gradient(#00e0ff ${pct}%, rgba(255,255,255,.07) ${pct}% 100%)`
      };
    },
  },
  mounted() {
    this.countUp(this.displayedXp, this.xp, 700);
    this.refreshStats();
    window.addEventListener("archive:changed", this.refreshStats);
    window.addEventListener("storage", this.refreshStats);
    this.initReels();
    this.startConfettiLoop(); // idle subtle confetti twinkle
  },
  beforeDestroy() {
    window.removeEventListener("archive:changed", this.refreshStats);
    window.removeEventListener("storage", this.refreshStats);
    cancelAnimationFrame(this.animRAF);
    cancelAnimationFrame(this._confettiRAF || 0);
  },
  methods: {
    formatDate(iso){
      try { return new Date(iso).toLocaleString(); }
      catch(e){ return iso || ""; }
    },
    countUp(from, to, ms = 600){
      cancelAnimationFrame(this.animRAF);
      const start = performance.now();
      const tick = (t) => {
        const k = Math.min(1, (t - start) / ms);
        const ease = 1 - Math.pow(1 - k, 3);
        this.displayedXp = Math.round(from + (to - from) * ease);
        if (k < 1) this.animRAF = requestAnimationFrame(tick);
      };
      this.animRAF = requestAnimationFrame(tick);
    },
    refreshStats(){
      const list = ArchiveAPI.list();
      this.recent = list.slice(0, 8);
      // totals
      const conversations = list.length;
      const messages = list.reduce((a,c) => a + (c.messages?.length || 0), 0);
      const kset = new Set();
      list.forEach(c => (c.keywords || []).forEach(k => kset.add(k)));
      this.totals = { conversations, messages, topics: kset.size };
      this.streak = this.computeStreak(list);
    },
    computeStreak(list){
      // days with at least one convo; count back-to-back days from today
      const days = new Set(list.map(c => (c.updatedAt || c.createdAt || "").slice(0,10)));
      if (!days.size) return 0;
      let streak = 0;
      let d = new Date();
      for (;;) {
        const key = d.toISOString().slice(0,10);
        if (days.has(key)) { streak++; d.setDate(d.getDate()-1); }
        else break;
      }
      return streak;
    },

    /* ---------- SLOT MACHINE ---------- */
    initReels(){
      // build tall strips so we can scroll
      for (let r=0;r<3;r++){
        const strip = [];
        for (let i=0;i<40;i++){
          strip.push(this.faces[i % this.faces.length]);
        }
        this.reelStrips.splice(r,1,strip);
      }
    },
    reelStyle( ){
      return {};
    },
    stripStyle(idx){
      const y = this.reelOffsets[idx] || 0;
      return { transform: `translateY(${y}px)` };
    },
    spin(){
      if (this.spinning) return;
      this.spinning = true;
      this.winText = "";
      // random stop positions
      for (let i=0;i<3;i++){
        this.stopTargets[i] = Math.floor(Math.random()*this.faces.length);
        // scroll distance to land symbol ~ multiply by symbol height (56px)
        const laps = 6 + i*2; // later reels spin longer
        const distance = (laps * this.faces.length + this.stopTargets[i]) * 56;
        // current offset normalized
        const cur = this.reelOffsets[i] % (this.faces.length*56);
        const targetY = -distance;
        // tween
        this.animateReel(i, cur, targetY, 1600 + i*400);
      }
      // evaluate result a bit after last reel stops
      setTimeout(this.finishSpin, 2100);
    },
    animateReel(i, from, to, ms){
      const start = performance.now();
      const tick = (t) => {
        const k = Math.min(1, (t - start) / ms);
        const ease = 1 - Math.pow(1 - k, 3);
        this.$set(this.reelOffsets, i, from + (to - from) * ease);
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    },
    finishSpin(){
      this.spinning = false;
      const a = this.stopTargets[0], b = this.stopTargets[1], c = this.stopTargets[2];
      let reward = 0;
      if (a === b && b === c) {
        this.winText = "JACKPOT! +100 XP 🎉";
        reward = 100;
        this.burstConfetti(140);
      } else if (a === b || b === c || a === c) {
        this.winText = "Nice Match! +35 XP ✨";
        reward = 35;
        this.burstConfetti(70);
      } else {
        this.winText = "Lucky Spin! +15 XP 🍀";
        reward = 15;
        this.burstConfetti(40);
      }
      const old = this.xp;
      this.$store.dispatch("addXP", reward);
      this.$nextTick(() => this.countUp(old, this.xp, 700));
    },

    /* ---------- CONFETTI ---------- */
    startConfettiLoop(){
      const cv = this.$refs.confetti;
      if (!cv) return;
      const ctx = cv.getContext("2d");
      const fit = () => { cv.width = cv.clientWidth; cv.height = cv.clientHeight; };
      fit(); window.addEventListener("resize", fit);
      const pieces = Array.from({length: 36}).map(() => ({
        x: Math.random()*cv.width, y: Math.random()*cv.height,
        r: 2 + Math.random()*3, vx: -0.3 + Math.random()*0.6, vy: 0.2 + Math.random()*0.6,
        c: ["#00e0ff","#7c6cff","#ff6bd6","#ffd36b"][Math.floor(Math.random()*4)]
      }));
      const loop = () => {
        ctx.clearRect(0,0,cv.width,cv.height);
        pieces.forEach(p => {
          p.x += p.vx; p.y += p.vy;
          if (p.y > cv.height) p.y = -6;
          if (p.x < -6) p.x = cv.width+6;
          if (p.x > cv.width+6) p.x = -6;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI*2); ctx.fillStyle = p.c; ctx.fill();
        });
        this._confettiRAF = requestAnimationFrame(loop);
      };
      loop();
    },
    burstConfetti(n=80){
      const cv = this.$refs.confetti; if (!cv) return;
      const ctx = cv.getContext("2d");
      const w = cv.width, h = cv.height;
      const parts = Array.from({length: n}).map(() => ({
        x: w/2, y: h/2, r: 2+Math.random()*3,
        vx: -3 + Math.random()*6, vy: -3 + Math.random()*6,
        g: 0.05 + Math.random()*0.08,
        c: ["#00e0ff","#7c6cff","#ff6bd6","#ffd36b"][Math.floor(Math.random()*4)],
        life: 60+Math.random()*30
      }));
      let f = 0;
      const boom = () => {
        ctx.clearRect(0,0,w,h);
        parts.forEach(p => {
          p.vy += p.g; p.x += p.vx; p.y += p.vy; p.life--;
          ctx.globalAlpha = Math.max(0, p.life/90);
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI*2); ctx.fillStyle = p.c; ctx.fill();
        });
        ctx.globalAlpha = 1;
        if (f++ < 90) requestAnimationFrame(boom);
      };
      boom();
    },
  }
};
</script>

<style scoped>
.wrap{ width:100%; height:100%; display:flex; flex-direction:column; }
.head{
  display:flex; align-items:center; justify-content:space-between;
  padding: 14px 16px; border-bottom: 1px solid rgba(255,255,255,.12);
  background: linear-gradient(180deg, rgba(0,224,255,.10), rgba(0,224,255,0) 40%);
}
.title{
  margin:0; font-size:20px; letter-spacing:.6px; color:#e9eaff;
  text-shadow: 0 0 10px rgba(0,224,255,.25), 0 0 16px rgba(124,108,255,.18);
}
.close{
  border:1px solid rgba(255,255,255,.2); border-radius:10px;
  background: rgba(255,255,255,.06); color:#fff;
  padding:6px 10px; cursor:pointer;
}
.grid{
  display:grid; grid-template-columns: 1.1fr 1fr; gap:14px; padding:14px;
  grid-template-rows: minmax(260px, 1fr) minmax(260px, 1fr);
}
.card{
  background: rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.12);
  border-radius: 14px; padding: 12px; position: relative; overflow:hidden;
  box-shadow: inset 0 0 0 1px rgba(0, 225, 255, 0.212);
}
.ring-card{ grid-row: span 2; display:flex; flex-direction:column; gap:12px; align-items:center; }
.ring{
  width: 240px; height: 240px; border-radius:50%;
  display:grid; place-items:center;
  box-shadow: 0 0 40px rgba(0,224,255,.2), inset 0 0 0 8px rgba(0,224,255,.08);
}
.ring-inner{
  width: 190px; height:190px; border-radius:50%;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  background: radial-gradient(60% 60% at 50% 45%, rgba(0,224,255,.12), rgba(255,255,255,.05));
  border: 1px solid rgba(255,255,255,.15);
  text-align:center;
}
.ring-inner .lvl{ font-size: 28px; font-weight:800; letter-spacing: 1px; }
.ring-inner .xp{ font-size: 18px; margin-top:4px; }
.ring-inner small{ opacity:.8; }

.stats{ width:100%; display:grid; grid-template-columns: repeat(4,1fr); gap:10px; }
.stat{
  background: rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.1);
  border-radius:10px; padding:10px; text-align:center;
}
.stat b{ font-size:20px; display:block; }
.stat small{ opacity:.85 }

.slot-card{ display:flex; flex-direction:column; gap:10px; }
.slot-title{ margin:2px 0 0; text-align:center; letter-spacing:.6px; }
.slots{
  height: 220px; border-radius: 14px; border:1px solid rgba(255,255,255,.12);
  background: radial-gradient(90% 100% at 50% -10%, rgba(0,224,255,.10), transparent 60%), rgba(0,0,0,.25);
  display:flex; align-items:center; justify-content:center; gap: 12px;
  box-shadow: inset 0 0 0 1px rgba(0,224,255,.08), 0 16px 60px rgba(0,0,0,.35);
}
.reel{
  width: 110px; height: 160px; overflow: hidden; border-radius: 12px;
  background: linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.03));
  border: 1px solid rgba(255,255,255,.14);
  display:grid; place-items:center; position: relative;
}
.strip{
  will-change: transform;
  transition: transform .2s cubic-bezier(.2,.8,.2,1);
}
.face{
  height: 56px; display:grid; place-items:center; font-size:34px;
  text-shadow: 0 0 10px rgba(0,224,255,.25);
}
.slot-actions{
  display:flex; align-items:center; justify-content:center; gap:12px;
}
.spin{
  padding:10px 18px; font-weight:800; letter-spacing:1px;
  background: linear-gradient(180deg, #00e0ff, #7c6cff);
  color:#0b0b1a; border:none; border-radius: 12px; cursor:pointer;
  box-shadow: 0 8px 32px rgba(0,224,255,.35);
}
.spin:disabled{ opacity:.6; cursor:not-allowed; }
.win{ font-weight:700; color:#ffd36b; text-shadow: 0 0 12px rgba(255,211,107,.55); }

.feed-card{ overflow:auto; }
.feed{ list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px; }
.feed li{
  display:grid; grid-template-columns: 160px 1fr auto; align-items:center;
  background: rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.1); border-radius:10px; padding:8px 10px;
}
.feed .when{ opacity:.85; font-size:12px; }
.feed .title{ padding:0 8px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.feed .chips em{ margin-left:8px; font-style:normal; opacity:.85; }
.confetti{ position:absolute; inset:0; pointer-events:none; }
@media (max-width: 980px){
  .grid{ grid-template-columns: 1fr; grid-template-rows: auto; }
  .ring-card{ grid-row:auto; }
}
</style>